import React, { useState, useEffect } from 'react';
import { ArrowUpTrayIcon, EyeIcon, TrashIcon } from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';

const DocumentsTab = ({ register, errors, watch, setValue, inputClassName, documents, setDocuments }) => {
  const [activeSubTab, setActiveSubTab] = useState('documents');
  const [showJobsFilter, setShowJobsFilter] = useState(true);
  const [showEstimatesFilter, setShowEstimatesFilter] = useState(true);
  
  useEffect(() => {
    setDocuments(watch('documents'))
    setValue('documents',watch('documents'))
  }, [watch,setValue,setDocuments])

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    
    const newDocuments = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      created_at: new Date().toISOString(),
      file: file
    }));
   
    setDocuments([...documents, ...newDocuments]);
    newDocuments.forEach((doc, index) => {
      const docIndex = documents.length + index;
      setValue(`documents.${docIndex}`, doc); // Update only the new index
    });
  };

  const removeDocument = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Are you sure you want to remove this document?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        const newDocuments = documents.filter((doc) => doc.id !== id);
        setDocuments(newDocuments);
        setValue('documents', newDocuments);
  
        const existingRemovedIds = watch('document_removed_ids') || [];
        const updatedRemovedIds = [...new Set([...existingRemovedIds, id])];
        setValue('document_removed_ids', updatedRemovedIds);
   
      }
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Mock data for jobs and estimates
  const mockJobs = [
    { id: 1, number: 'JOB-001', title: 'Kitchen Sink Repair', date: '2024-01-15', pictures: 3 },
    { id: 2, number: 'JOB-002', title: 'Bathroom Faucet Installation', date: '2024-01-20', pictures: 5 },
    { id: 3, number: 'JOB-003', title: 'Water Heater Maintenance', date: '2024-02-01', pictures: 2 }
  ];

  const mockEstimates = [
    { id: 1, number: 'EST-001', title: 'Kitchen Remodel', date: '2024-01-10', pictures: 4 },
    { id: 2, number: 'EST-002', title: 'Bathroom Upgrade', date: '2024-01-25', pictures: 6 },
    { id: 3, number: 'EST-003', title: 'Plumbing System Update', date: '2024-02-05', pictures: 3 }
  ];

  const filteredJobs = showJobsFilter ? mockJobs : [];
  const filteredEstimates = showEstimatesFilter ? mockEstimates : [];

  return (
    <div className="space-y-8">
      {/* Document Manager */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Customer Document Manager</h3>
        
        {/* Sub-tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
             type="button"
              onClick={() => setActiveSubTab('documents')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeSubTab === 'documents'
                  ? 'border-[#009efb] text-[#009efb]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Documents
            </button>
            <button
             type="button"
              onClick={() => setActiveSubTab('pictures')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeSubTab === 'pictures'
                  ? 'border-[#009efb] text-[#009efb]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Job & Estimate Pictures
            </button>
          </nav>
        </div>

        {/* Documents Tab Content */}
        {activeSubTab === 'documents' && (
          <div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload New Documents
              </label>
              <div className="flex items-center justify-center w-full">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <ArrowUpTrayIcon className="w-8 h-8 mb-2 text-gray-500" />
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">PDF, DOC, DOCX, JPG, PNG (MAX. 10MB each)</p>
                  </div>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  />
                </label>
              </div>
            </div>

            {documents.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No documents uploaded yet</p>
            ) : (
              <div className="space-y-2">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-white border border-gray-200 rounded-lg space-y-2 sm:space-y-0 sm:space-x-4"
                  >
                    {/* File Info */}
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-600">
                            {doc.name.split('.').pop().toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 break-all">{doc.name}</p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(doc.size)} • Uploaded {formatDate(doc.created_at)}
                        </p>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-4">
                      <button
                        type="button"
                        className="text-[#009efb] hover:text-[#0089db]"
                        onClick={() =>
                          window.open(doc.url ? doc.url : URL.createObjectURL(doc.file), '_blank')
                        }
                      >
                        <EyeIcon className="w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        className="text-red-600 hover:text-red-800"
                        onClick={() => removeDocument(doc.id)}
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}

              </div>
            )}
          </div>
        )}

        {/* Job & Estimate Pictures Tab Content */}
        {activeSubTab === 'pictures' && (
          <div>
            {/* Filters */}
            <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Stored Pictures Filters</h4>
              <div className="flex space-x-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={showJobsFilter}
                    onChange={(e) => setShowJobsFilter(e.target.checked)}
                    className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-900">Jobs</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={showEstimatesFilter}
                    onChange={(e) => setShowEstimatesFilter(e.target.checked)}
                    className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-900">Estimates</span>
                </label>
              </div>
            </div>

            {/* Jobs */}
            {filteredJobs.length > 0 && (
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-900 mb-3">Jobs</h4>
                <div className="space-y-3">
                  {filteredJobs.map((job) => (
                    <div key={job.id} className="p-4 bg-white border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <h5 className="font-medium text-gray-900">{job.number}</h5>
                          <p className="text-sm text-gray-600">{job.title}</p>
                          <p className="text-xs text-gray-500">Date: {formatDate(job.date)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">{job.pictures} pictures</p>
                          <button
                            type="button"
                            className="text-[#009efb] hover:text-[#0089db] text-sm"
                          >
                            View Pictures
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Estimates */}
            {filteredEstimates.length > 0 && (
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Estimates</h4>
                <div className="space-y-3">
                  {filteredEstimates.map((estimate) => (
                    <div key={estimate.id} className="p-4 bg-white border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <h5 className="font-medium text-gray-900">{estimate.number}</h5>
                          <p className="text-sm text-gray-600">{estimate.title}</p>
                          <p className="text-xs text-gray-500">Date: {formatDate(estimate.date)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">{estimate.pictures} pictures</p>
                          <button
                            type="button"
                            className="text-[#009efb] hover:text-[#0089db] text-sm"
                          >
                            View Pictures
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {filteredJobs.length === 0 && filteredEstimates.length === 0 && (
              <p className="text-gray-500 text-center py-4">No pictures found</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsTab; 